import React, { useEffect, useState } from 'react';
import client, { apiCall } from '../../api/client';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Loader from '../../components/Loader';

const EMPTY_FORM = { method: '', cost: 1, description: '' };

export default function MethodCosts() {
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    apiCall(client.get('/method_cost/admin/get_list'))
      .then(setMethods)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.post('/method_cost/admin/create', { ...form, cost: +form.cost }));
      setForm(EMPTY_FORM);
      setShowAdd(false);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiCall(client.put('/method_cost/admin/update', { ...editing, cost: +editing.cost }));
      setEditing(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row) => {
    try {
      await apiCall(client.patch('/method_cost/admin/update_status', { uniqueId: row.uniqueId, status: !row.status }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const columns = [
    { key: 'method', label: 'Method', render: (row) => <code>{row.method}</code> },
    { key: 'cost', label: 'Cost' },
    { key: 'description', label: 'Description', render: (row) => row.description || '—' },
    { key: 'status', label: 'Status', render: (row) => <StatusBadge value={row.status} /> },
    {
      key: 'actions', label: '', render: (row) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={() => setEditing(row)}>Edit</button>
          <button className="btn btn-outline btn-sm" onClick={() => toggleStatus(row)}>
            {row.status ? 'Disable' : 'Enable'}
          </button>
        </div>
      )
    },
  ];

  return (
    <div>
      <div className="page-header-row">
        <div>
          <h1>Method Costs</h1>
          <p className="form-hint" style={{ marginTop: 4 }}>
            Drives the public API Credits page. Any method not listed here is charged the default cost.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setShowAdd(true); }}>+ Add Method Cost</button>
      </div>
      {error && <div className="form-error">{error}</div>}

      <div className="card">
        {loading ? (
          <Loader label="Loading method costs..." />
        ) : (
          <DataTable columns={columns} rows={methods} emptyMessage="No method costs configured yet." />
        )}
      </div>

      {showAdd && (
        <div className="modal-backdrop" onClick={() => setShowAdd(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Add method cost</h3>
            <form onSubmit={handleCreate}>
              <div className="form-group"><label>Method</label>
                <input name="method" required value={form.method} onChange={handleChange} placeholder="eth_call" /></div>
              <div className="form-group"><label>Cost</label>
                <input name="cost" type="number" min="0" required value={form.cost} onChange={handleChange} /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Description</label>
                <input name="description" value={form.description} onChange={handleChange} placeholder="Optional" /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Adding...' : 'Add'}</button>
                <button type="button" className="btn btn-outline" onClick={() => setShowAdd(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={() => setEditing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Edit method cost</h3>
            <form onSubmit={handleUpdate}>
              <div className="form-group"><label>Method</label>
                <input required value={editing.method} onChange={(e) => setEditing({ ...editing, method: e.target.value })} /></div>
              <div className="form-group"><label>Cost</label>
                <input type="number" min="0" required value={editing.cost} onChange={(e) => setEditing({ ...editing, cost: e.target.value })} /></div>
              <div className="form-group" style={{ marginBottom: 20 }}><label>Description</label>
                <input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary" disabled={saving}>Save</button>
                <button type="button" className="btn btn-outline" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
